import { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthHeaders, isAdminLoggedIn, getAdminUser, extractCreatorId } from '../utils/authUtils';
import { getApiBaseUrlWithoutPath } from '../../config/environment';
import { adminUserService } from '../../services/adminUserService';
import {
  readAdminNotificationFeedPrefs,
  ADMIN_NOTIFICATION_FEED_PREFS_CHANGED,
} from '../utils/adminNotificationFeedPrefs';
import { hasAccess } from '../constants/teamAccessPermissions';

const lastReadStorageKey = () => {
  const u = getAdminUser();
  const id = u?._id || u?.id || u?.email || 'default';
  return `adminNotifLastReadAt:${id}`;
};

const readLastReadAt = () => {
  const v = parseInt(localStorage.getItem(lastReadStorageKey()) || '0', 10);
  return Number.isFinite(v) ? v : 0;
};

export const markNotificationsRead = () => {
  localStorage.setItem(lastReadStorageKey(), String(Date.now()));
};

const isOrderWithMyProducts = (order, myId) => {
  if (!myId) return true;
  const items = order.items || [];
  return items.some((item) => {
    const product = item.product || item.productId;
    if (!product || typeof product !== 'object') return false;
    return extractCreatorId(product.createdBy) === myId;
  });
};

const isMyProduct = (product, myId) => {
  if (!myId) return true;
  return extractCreatorId(product.createdBy) === myId;
};

function productTotalStock(product) {
  let totalStock = 0;
  if (product?.stock && typeof product.stock.size === 'number' && product.stock.size > 0) {
    for (const [, sizeMap] of product.stock) {
      for (const [, quantity] of sizeMap) {
        totalStock += quantity || 0;
      }
    }
  } else if (typeof product?.totalStock === 'number') {
    totalStock = product.totalStock;
  }
  return totalStock;
}

/** Team cannot use GET /admin/analytics — build bell feed from allowed list APIs. */
async function fetchTeamNotificationContext(headers, apiUrl, user, prefs) {
  const data = { recentOrders: [], lowStockProducts: [] };
  if (prefs.orderNotifications && hasAccess('Orders|View', user)) {
    try {
      const res = await fetch(`${apiUrl}/api/admin/orders?limit=10&page=1`, {
        method: 'GET',
        headers,
      });
      if (res.ok) {
        const j = await res.json().catch(() => ({}));
        if (j.success && Array.isArray(j.data)) data.recentOrders = j.data;
      }
    } catch {
      /* ignore */
    }
  }
  if (prefs.stockNotifications && hasAccess('Products|View', user)) {
    try {
      const res = await fetch(`${apiUrl}/api/admin/products?limit=80&page=1`, {
        method: 'GET',
        headers,
      });
      if (res.ok) {
        const j = await res.json().catch(() => ({}));
        const products = Array.isArray(j.data) ? j.data : [];
        data.lowStockProducts = products.filter((p) => {
          const total = productTotalStock(p);
          return total > 0 && total <= 10;
        });
      }
    } catch {
      /* ignore */
    }
  }
  return data;
}

/**
 * Loads admin analytics + recent users for the header bell (no background interval — avoids periodic “refresh” feel).
 * Refetch: on mount, when tab becomes visible, prefs/storage change, or bell menu refresh.
 * "Unread" = items newer than last time the dropdown was opened (localStorage per admin user).
 */
const useAdminNotifications = (currentUser, enabled = true) => {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const mounted = useRef(true);
  const hasLoadedOnce = useRef(false);

  const isTeam = currentUser?.role === 'team';
  const myId = String(currentUser?._id || currentUser?.id || '');

  const buildList = useCallback(
    (analyticsData, usersPayload, prefs) => {
      const list = [];
      const now = Date.now();
      const showOrder = prefs.orderNotifications;
      const showStock = prefs.stockNotifications;
      const showUser = prefs.userNotifications;

      if (showOrder) {
        let recentOrders = analyticsData?.recentOrders || [];
        if (isTeam && myId) {
          recentOrders = recentOrders.filter((o) => isOrderWithMyProducts(o, myId));
        }
        recentOrders.slice(0, 10).forEach((order) => {
          const id = order._id || order.id;
          if (!id) return;
          const at = new Date(order.createdAt || order.date || 0).getTime() || now;
          list.push({
            key: `order-${id}`,
            type: 'order',
            at,
            title: `New order #${order.orderNumber || String(id).slice(-6)}`,
            subtitle: `${order.status || 'Order Placed'} · PKR ${Number(order.total || 0).toLocaleString()}`,
            navigate: { view: 'orders', params: { openOrderId: String(id) } },
          });
        });
      }

      if (showStock) {
        let lowStock = analyticsData?.lowStockProducts || [];
        if (isTeam && myId) {
          lowStock = lowStock.filter((p) => isMyProduct(p, myId));
        }
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        lowStock.slice(0, 6).forEach((product, i) => {
          const id = product._id || product.id;
          if (!id) return;
          const at =
            new Date(product.updatedAt || product.createdAt || 0).getTime() ||
            now - (i + 1) * 60000;
          const productId = String(id);
          list.push({
            key: `stock-${id}`,
            type: 'stock',
            at,
            title: `Low stock: ${product.name || 'Product'}`,
            subtitle: 'View product on store (new tab)',
            openInNewTab: true,
            href: origin ? `${origin}/product/${productId}` : `/product/${productId}`,
          });
        });
      }

      if (showUser) {
        let users = usersPayload?.data || usersPayload?.users || [];
        if (!Array.isArray(users)) users = [];
        users = [...users].sort(
          (a, b) =>
            new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
        );
        const userNewMaxAgeMs = 7 * 24 * 60 * 60 * 1000;
        users.slice(0, 12).forEach((user) => {
          const id = user._id || user.id;
          if (!id) return;
          const created = new Date(user.createdAt || 0).getTime();
          if (!created || now - created > userNewMaxAgeMs) return;
          const at = created || now;
          list.push({
            key: `user-${id}`,
            type: 'user',
            at,
            title: `New user: ${user.name || user.email || 'User'}`,
            subtitle: user.email || 'Registered',
            navigate: { view: 'users', params: { openUserId: String(id) } },
          });
        });
      }

      list.sort((a, b) => b.at - a.at);
      return list.slice(0, 18);
    },
    [isTeam, myId]
  );

  const fetchFeed = useCallback(async () => {
    if (!enabled || !isAdminLoggedIn()) return;

    try {
      if (!hasLoadedOnce.current) setLoading(true);
      setError(null);
      const prefs = readAdminNotificationFeedPrefs();
      const needAnalytics = prefs.orderNotifications || prefs.stockNotifications;
      const canListUsers = !isTeam || hasAccess('Users|View', currentUser);
      const needUsers = !!prefs.userNotifications && canListUsers;

      if (!needAnalytics && !needUsers) {
        if (!mounted.current) return;
        setItems([]);
        setUnreadCount(0);
        setLastFetchedAt(Date.now());
        hasLoadedOnce.current = true;
        setError(null);
        return;
      }

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      const [analyticsOutcome, usersOutcome] = await Promise.allSettled([
        needAnalytics && !isTeam
          ? fetch(`${apiUrl}/api/admin/analytics`, { method: 'GET', headers })
          : Promise.resolve(null),
        needUsers
          ? adminUserService.getUsers({ page: 1, limit: 12, sortBy: 'createdAt', sortOrder: 'desc' })
          : Promise.resolve(null),
      ]);

      let analyticsData = null;
      if (needAnalytics && isTeam) {
        analyticsData = await fetchTeamNotificationContext(
          headers,
          apiUrl,
          currentUser,
          prefs
        );
      } else if (
        needAnalytics &&
        analyticsOutcome.status === 'fulfilled' &&
        analyticsOutcome.value &&
        analyticsOutcome.value.ok
      ) {
        const j = await analyticsOutcome.value.json().catch(() => ({}));
        if (j.success && j.data) analyticsData = j.data;
      }

      let usersPayload = null;
      if (
        needUsers &&
        usersOutcome.status === 'fulfilled' &&
        usersOutcome.value?.success
      ) {
        usersPayload = usersOutcome.value;
      }

      const merged = buildList(analyticsData || {}, usersPayload || {}, prefs);
      const lastRead = readLastReadAt();
      const unread = merged.filter((n) => n.at > lastRead).length;

      if (!mounted.current) return;
      setItems(merged);
      setUnreadCount(unread);
      setLastFetchedAt(Date.now());
      hasLoadedOnce.current = true;
    } catch (e) {
      if (mounted.current) setError(e.message || 'Failed to load notifications');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [enabled, buildList, currentUser, isTeam]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchFeed();
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchFeed();
    };
    const onPrefsChanged = () => fetchFeed();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(ADMIN_NOTIFICATION_FEED_PREFS_CHANGED, onPrefsChanged);
    window.addEventListener('storage', onPrefsChanged);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(ADMIN_NOTIFICATION_FEED_PREFS_CHANGED, onPrefsChanged);
      window.removeEventListener('storage', onPrefsChanged);
    };
  }, [enabled, fetchFeed]);

  const refreshUnreadFromStorage = useCallback(() => {
    const lastRead = readLastReadAt();
    setUnreadCount(items.filter((n) => n.at > lastRead).length);
  }, [items]);

  return {
    items,
    unreadCount,
    loading,
    error,
    lastFetchedAt,
    refetch: fetchFeed,
    refreshUnreadFromStorage,
  };
};

export default useAdminNotifications;
